import {
  mapRoomMatchToSummary,
  mapSummaryToRoomMatchFields,
  buildCompetitionPermissionGrants,
} from "../../../adapters/cmoe/roomSessionAdapter";
import { computeActiveParticipantEntryIds } from "../../roomGovernance/roomGovernanceLogic";
import { managedByFromLease } from "../lease/managerLease";
import { getRoomSessionPolicyBundle } from "../policy/roomSessionPolicyBundle";

/**
 * @param {string[] | null | undefined} a
 * @param {string[] | null | undefined} b
 */
function sameStringSet(a, b) {
  const sa = [...new Set(a || [])].sort();
  const sb = [...new Set(b || [])].sort();
  if (sa.length !== sb.length) return false;
  return sa.every((v, i) => v === sb[i]);
}

/**
 * Participant entry ids implied by CMOE match summary bindings.
 * @param {import('../match/matchTypes').MatchSummary | null | undefined} summary
 */
export function participantIdsFromSummaryBindings(summary) {
  if (!summary?.bindings) return [];
  const ids = new Set();
  for (const v of Object.values(summary.bindings)) {
    if (v) ids.add(String(v));
  }
  return [...ids];
}

/**
 * @param {import('../../roomGovernance/roomGovernanceTypes').RoomSessionMatch[]} v1Matches
 * @param {import('../match/matchTypes').MatchSummary[]} cmoeSummaries
 */
export function compareRoomMatchInventory(v1Matches, cmoeSummaries) {
  const v1Ids = (v1Matches || []).map((m) => m.id).sort();
  const cmoeIds = (cmoeSummaries || []).map((s) => s.matchId).sort();
  const ok = sameStringSet(v1Ids, cmoeIds);
  return {
    ok,
    expected: v1Ids,
    actual: cmoeIds,
    details: {
      missingInCmoe: v1Ids.filter((id) => !cmoeIds.includes(id)),
      extraInCmoe: cmoeIds.filter((id) => !v1Ids.includes(id)),
    },
  };
}

/**
 * @param {import('../../roomGovernance/roomGovernanceTypes').RoomSessionMatch} v1Match
 * @param {import('../match/matchTypes').MatchSummary | null | undefined} summary
 */
export function compareMatchStatusAndManager(v1Match, summary) {
  const expectedStatus = v1Match.status;
  const fromCmoe = summary ? mapSummaryToRoomMatchFields(summary).status : null;
  const statusOk = fromCmoe === expectedStatus;

  const expectedManager = v1Match.managedBy || "";
  const actualManager = summary
    ? managedByFromLease(summary.managerLease) || summary.managerLease?.holderUid || ""
    : "";
  const managerOk = expectedManager === actualManager;

  return {
    status: {
      ok: statusOk,
      expected: expectedStatus,
      actual: fromCmoe,
    },
    manager: {
      ok: managerOk,
      expected: expectedManager,
      actual: actualManager,
    },
  };
}

/**
 * @param {import('../../roomGovernance/roomGovernanceTypes').RoomSessionMatch} v1Match
 * @param {import('../match/matchTypes').MatchSummary | null | undefined} summary
 */
export function compareParticipantBindings(v1Match, summary) {
  const expected = [...(v1Match.participantEntryIds || [])].sort();
  const actual = participantIdsFromSummaryBindings(summary).sort();
  const ok =
    v1Match.status === "completed"
      ? actual.length === 0
      : sameStringSet(expected, actual);
  return {
    ok,
    expected: v1Match.status === "completed" ? [] : expected,
    actual,
  };
}

/**
 * @param {import('../../roomGovernance/roomGovernanceTypes').RoomSessionMatch} v1Match
 * @param {import('../match/matchTypes').MatchSummary | null | undefined} summary
 */
export function compareMatchCompletion(v1Match, summary) {
  if (v1Match.status !== "completed") {
    return { ok: true, skipped: true, reason: "v1_not_completed" };
  }
  const cmoeCompleted = summary?.orchestrationState === "completed";
  const bindingsCleared = !summary?.bindings || Object.keys(summary.bindings).length === 0;
  return {
    ok: Boolean(cmoeCompleted && bindingsCleared),
    expected: { orchestrationState: "completed", bindings: {} },
    actual: {
      orchestrationState: summary?.orchestrationState,
      bindings: summary?.bindings,
    },
  };
}

/**
 * @param {import('../../roomGovernance/roomGovernanceTypes').RoomSessionMatch[]} v1Matches
 * @param {import('../allocation/allocationTypes').AllocationHold[]} activeHolds
 * @param {boolean} ledgerEnabled
 */
export function compareAllocationState(v1Matches, activeHolds, ledgerEnabled) {
  if (!ledgerEnabled) {
    return { ok: true, skipped: true, reason: "allocation_ledger_flag_off" };
  }
  const liveIds = computeActiveParticipantEntryIds(v1Matches || []);
  const holdsByParticipant = {};
  for (const h of activeHolds || []) {
    if (h.state !== "held" || h.targetType !== "participant") continue;
    holdsByParticipant[h.targetId] = h.matchId;
  }
  const mismatches = [];
  for (const pid of liveIds) {
    const holdMatch = holdsByParticipant[pid];
    if (!holdMatch) {
      mismatches.push({ participantId: pid, issue: "missing_hold" });
    }
  }
  for (const [pid, matchId] of Object.entries(holdsByParticipant)) {
    if (!liveIds.has(pid)) {
      mismatches.push({ participantId: pid, matchId, issue: "orphan_hold" });
    }
  }
  return {
    ok: mismatches.length === 0,
    expected: { liveParticipantIds: [...liveIds].sort() },
    actual: { holdsByParticipant, mismatches },
  };
}

/**
 * @param {import('../../roomGovernance/roomGovernanceTypes').RoomSessionMatch} v1Match
 * @param {Record<string, unknown> | null | undefined} activityDoc
 * @param {boolean} activityFlagOn
 */
export function compareActivityAttachment(v1Match, activityDoc, activityFlagOn) {
  if (!activityFlagOn) {
    return { ok: true, skipped: true, reason: "activity_module_flag_off" };
  }
  if (v1Match.status !== "live") {
    return { ok: true, skipped: true, reason: "v1_not_live" };
  }
  const mod = getRoomSessionPolicyBundle().activity.defaultModule;
  const hasDoc = Boolean(activityDoc);
  const ref = activityDoc?.activityModuleRef;
  const refOk =
    ref &&
    ref.moduleId === mod.moduleId &&
    String(ref.moduleVersion) === String(mod.moduleVersion);
  return {
    ok: hasDoc && refOk,
    expected: { attached: true, activityModuleRef: mod },
    actual: { hasDoc, activityModuleRef: ref || null },
  };
}

/**
 * @param {{ createdBy?: string, deputyRefereeUid?: string | null }} roomDoc
 * @param {{ principalId?: string, permissions?: string[] }[]} cmoeGrants
 */
export function comparePermissionGrants(roomDoc, cmoeGrants) {
  const expected = buildCompetitionPermissionGrants(roomDoc || {});
  const byUid = new Map();
  for (const g of cmoeGrants || []) {
    if (g.principalId) byUid.set(g.principalId, [...(g.permissions || [])].sort());
  }
  const mismatches = [];
  for (const exp of expected) {
    const actual = byUid.get(exp.uid);
    if (!actual || !sameStringSet(exp.permissions, actual)) {
      mismatches.push({ uid: exp.uid, expected: exp.permissions.sort(), actual: actual || [] });
    }
  }
  return {
    ok: mismatches.length === 0,
    expected: expected.map((e) => ({ uid: e.uid, permissions: [...e.permissions].sort() })),
    actual: [...byUid.entries()].map(([uid, permissions]) => ({ uid, permissions })),
    details: { mismatches },
  };
}

/**
 * Build expected summary shape from V1 (adapter projection) for focused diff.
 * @param {import('../../roomGovernance/roomGovernanceTypes').RoomSessionMatch} v1Match
 * @param {string} competitionId
 */
export function expectedSummaryFromV1(v1Match, competitionId) {
  return mapRoomMatchToSummary(v1Match, competitionId);
}
